<script setup lang="ts">
import { SolButton } from '@solfacil/girassol'
import { useI18n } from 'vue-i18n'
import ChevronRightIcon from '~icons/material-symbols/chevron-right'
import RefreshIcon from '~icons/material-symbols/refresh'
import useAuth from '~/store-v2/auth/useAuth'
import { useImpersonateStore } from '~/store/impersonate'
import ProjectsInProgressEmpty from './ProjectsInProgressEmpty.vue'
import ProjectsInProgressNotImpersonate from './ProjectsInProgressNotImpersonate.vue'
import ProjectsInProgressSkeleton from './ProjectsInProgressSkeleton.vue'
import { useProjectsInProgress } from './useProjectsInProgress'

const { data: auth } = useAuth()
const impersonateStore = useImpersonateStore()
const { t } = useI18n()

const isAdminNotImpersonating = computed(() => {
  return auth.value?.me?.perfil === 'admin' && !impersonateStore.verifyIsImpersonated()
})

const {
  isLoading,
  isEmpty,
  isRefreshing,
  displayData,
  progressBarWidth,
  handleRefresh,
  handleViewAll,
  handleTotalClick,
  handleCategoryClick,
  handleBreakdownClick,
  getBreakdownMobileLabel,
} = useProjectsInProgress()
</script>

<template>
  <SharedCardSession :title="t('overview.projects_in_progress.title')">
    <template #actions>
      <div class="actions-container">
        <div
          v-if="(!isEmpty || isLoading) && !isAdminNotImpersonating"
          class="flex items-center justify-end gap-2"
        >
          <span class="fonts-body-small-regular text-neutral-low-light whitespace-nowrap">
            {{ displayData.lastUpdated }}
          </span>
          <SolButton
            id="refresh-projects"
            class="refresh-button"
            size="small"
            variant="secondary"
            :loading="isRefreshing"
            @click="handleRefresh"
          >
            <template #icon-left>
              <RefreshIcon class="refresh-icon" />
            </template>
          </SolButton>
        </div>

        <SolButton
          v-if="!isAdminNotImpersonating"
          id="view-all-projects"
          size="small"
          variant="secondary"
          @click="handleViewAll"
        >
          {{ t('overview.projects_in_progress.view_all') }}
        </SolButton>
      </div>
    </template>
    <template #content>
      <div class="projects-in-progress">
        <ProjectsInProgressNotImpersonate v-if="isAdminNotImpersonating" />

        <ProjectsInProgressSkeleton v-else-if="isLoading" />

        <ProjectsInProgressEmpty v-else-if="isEmpty" />

        <template v-else>
          <div class="projects-in-progress-summary">
            <div class="summary-total">
              <div class="total-content">
                <p class="total-number">
                  {{ displayData.status.total }}
                </p>
                <div class="total-label">
                  <span
                    class="label-text"
                    @click="handleTotalClick"
                  >{{ t('overview.projects_in_progress.total') }}</span>
                  <ChevronRightIcon class="chevron-icon" />
                </div>
              </div>
              <div class="total-progress-bar">
                <div
                  v-for="category in displayData.categories"
                  :key="category.label"
                  class="progress-segment"
                  :class="{
                    'progress-in-progress': category.label === t('overview.projects_in_progress.categories.simulations'),
                    'progress-to-retrieve': category.label === t('overview.projects_in_progress.categories.proposals'),
                    'progress-installations': category.label === t('overview.projects_in_progress.categories.installations'),
                  }"
                  :style="{ width: `${progressBarWidth(category.total, displayData.status.total)}%` }"
                />
              </div>
            </div>
            <div class="summary-status">
              <div class="status-item">
                <p class="status-number">
                  {{ displayData.status.inProgress }}
                </p>
                <p class="status-label">
                  {{ t('overview.projects_in_progress.status.in_progress') }}
                </p>
                <div class="status-indicator status-in-progress" />
              </div>
              <div class="status-item">
                <p class="status-number">
                  {{ displayData.status.toRetrieve }}
                </p>
                <p class="status-label">
                  {{ t('overview.projects_in_progress.status.to_retrieve') }}
                </p>
                <div class="status-indicator status-to-retrieve" />
              </div>
            </div>
          </div>

          <div class="projects-in-progress-categories-scroll">
            <div class="projects-in-progress-categories">
              <div
                v-for="category in displayData.categories"
                :key="category.label"
                class="category-item"
              >
                <div class="category-item-scroll">
                  <div class="category-left">
                    <div class="category-content">
                      <p class="category-number">
                        {{ category.total }}
                      </p>
                      <div class="category-label">
                        <span
                          class="label-text"
                          @click="handleCategoryClick(category.label)"
                        >{{ category.label }}</span>
                        <ChevronRightIcon class="chevron-icon" />
                      </div>
                    </div>
                    <div class="category-progress-bar">
                      <div
                        class="progress-segment"
                        :class="{
                          'progress-in-progress': category.label === t('overview.projects_in_progress.categories.simulations'),
                          'progress-to-retrieve': category.label === t('overview.projects_in_progress.categories.proposals'),
                          'progress-installations': category.label === t('overview.projects_in_progress.categories.installations'),
                        }"
                        :style="{ width: `${progressBarWidth(category.total, displayData.status.total)}%` }"
                      />
                    </div>
                  </div>
                  <div class="category-breakdowns">
                    <button
                      v-for="breakdown in category.breakdowns"
                      :key="breakdown.label"
                      class="breakdown-card"
                      :class="{ '-highlighted': breakdown.highlighted }"
                      @click="handleBreakdownClick(category.label, breakdown.label)"
                    >
                      <div class="breakdown-content">
                        <p class="breakdown-number">
                          {{ breakdown.count }}
                        </p>
                        <p class="breakdown-label">
                          <span class="breakdown-label-full">{{ breakdown.label }}</span>
                          <span class="breakdown-label-mobile">{{ getBreakdownMobileLabel(breakdown.label) }}</span>
                        </p>
                      </div>
                      <ChevronRightIcon class="breakdown-chevron" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </template>
  </SharedCardSession>
</template>

<style scoped lang="scss">
.actions-container {
  @apply flex items-start justify-end gap-2 flex-wrap gap-2;

  @screen sm:system {
    @apply flex-nowrap items-center justify-between;
  }
}
.projects-in-progress {
  @apply flex flex-col gap-6;

  .projects-in-progress-summary {
    @apply flex items-start justify-between gap-8;

    .summary-total {
      @apply flex flex-col gap-4 w-28;
      @apply bg-transparent border-0 p-0 text-left;

      .total-content {
        @apply flex flex-col gap-1;

        .total-number {
          @apply fonts-heading-h4 text-neutral-low-pure;
        }

        .total-label {
          @apply flex items-center gap-1;

          .label-text {
            @apply fonts-body-medium-bold text-neutral-low-light cursor-pointer;
            @apply transition-colors;

            &:hover {
              @apply text-brand-primary-pure;
            }
          }

          .chevron-icon {
            @apply w-4 h-4 text-neutral-low-medium;
          }
        }
      }

      .total-progress-bar {
        @apply bg-neutral-high-light h-1.5 rounded-full;
        @apply flex overflow-hidden;

        .progress-segment {
          @apply h-full;

          &.progress-in-progress {
            @apply bg-brand-secondary-medium;
          }

          &.progress-to-retrieve {
            @apply bg-brand-secondary-pure;
          }

          &.progress-installations {
            @apply bg-brand-secondary-dark;
          }
        }
      }
    }

    .summary-status {
      @apply flex gap-8 items-end self-stretch;
      @apply hidden;

      @screen sm:system {
        @apply flex;
      }

      .status-item {
        @apply flex flex-col items-start;

        .status-number {
          @apply overflow-hidden overflow-ellipsis whitespace-nowrap;
          @apply fonts-heading-h4 text-neutral-low-pure;
          font-weight: 500;
          line-height: 18.4px;
        }

        .status-label {
          @apply fonts-body-small-regular text-neutral-low-light;
          @apply overflow-hidden overflow-ellipsis whitespace-nowrap;
        }

        .status-indicator {
          @apply h-2 rounded-lg w-7.5 mt-2;

          &.status-in-progress {
            @apply bg-neutral-high-pure border border-brand-primary-medium;
          }

          &.status-to-retrieve {
            @apply bg-brand-secondary-light border border-brand-secondary-pure;
          }
        }
      }
    }
  }

  .projects-in-progress-categories-scroll {
    @apply overflow-x-auto overflow-y-visible;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .projects-in-progress-categories {
    @apply flex flex-col gap-6 self-stretch;

    .category-item {
      @apply flex flex-col self-stretch;

      .category-item-scroll {
        @apply flex gap-4 items-stretch;

        .category-left {
          @apply flex flex-col gap-4 w-28 pt-2 flex-shrink-0;
          @apply bg-transparent border-0 p-0 text-left;
          scroll-snap-align: start;

          .category-content {
            @apply flex flex-col gap-1;

            .category-number {
              @apply fonts-heading-h2 text-neutral-low-pure;
            }

            .category-label {
              @apply flex items-center gap-1;

              .label-text {
                @apply fonts-body-medium-bold text-neutral-low-light cursor-pointer;
                @apply transition-colors;

                &:hover {
                  @apply text-brand-primary-pure;
                }
              }

              .chevron-icon {
                @apply w-4 h-4 text-neutral-low-medium;
              }
            }
          }

          .category-progress-bar {
            @apply bg-neutral-high-light h-1.5 rounded-full;
            @apply overflow-hidden;

            .progress-segment {
              @apply h-full;

              &.progress-in-progress {
                @apply bg-brand-secondary-medium;
              }

              &.progress-to-retrieve {
                @apply bg-brand-secondary-pure;
              }

              &.progress-installations {
                @apply bg-brand-secondary-dark;
              }
            }
          }
        }

        .category-breakdowns {
          @apply flex gap-2 self-stretch flex-1;

          .breakdown-card {
            @apply flex items-center gap-2;
            @apply bg-neutral-high-pure border border-brand-primary-medium rounded-md;
            @apply pl-4 pr-2 h-full flex-1;
            @apply transition-all;
            @apply min-w-33;
            scroll-snap-align: start;
            cursor: pointer;
            border-width: 1px;

            &:hover {
              box-shadow: inset 0 0 0 3px #DEDEDE;
            }

            &.-highlighted {
              @apply bg-brand-secondary-light;
              border-color: #00B569;
              border-width: 1px;

              &:hover {
                box-shadow: inset 0 0 0 3px #D3FF70;
              }
            }

            .breakdown-content {
              @apply flex flex-col gap-0.5 flex-1 text-left;

              .breakdown-number {
                @apply fonts-heading-h4 text-neutral-low-pure;
              }

              .breakdown-label {
                @apply fonts-body-medium-regular text-neutral-low-medium;

                .breakdown-label-full {
                  @apply block;
                }

                .breakdown-label-mobile {
                  @apply hidden;
                }
              }
            }

            .breakdown-chevron {
              @apply w-6 h-6 text-neutral-low-medium flex-shrink-0;
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: 360px) {
    .category-breakdowns {
      .breakdown-card {
        .breakdown-label {
          .breakdown-label-full {
            @apply hidden !important;
          }

          .breakdown-label-mobile {
            @apply block !important;
          }
        }
      }
    }
  }
}

@screen max-md:system {
  .projects-in-progress-summary {
    @apply flex-col gap-4;

    .summary-status {
      @apply w-full justify-between;
    }
  }
}

@screen max-sm:system {
  .projects-in-progress {
    @apply p-4;
  }

  .category-item {
    @apply flex-col;

    .category-left {
      @apply w-full;
    }

    .category-breakdowns {
      @apply w-full;
    }
  }
}
</style>
